import React, { useState, useCallback, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useT } from "@transifex/react";
import { useQuestionnaire } from "../../context/questionnaire";
import {
  Answer,
  QuestionData,
  AnswerMap,
  ExitModalConfig,
} from "../../context/questionnaire/types";
import {
  evaluateConditions,
  getPageErrors,
  getVisiblePages,
} from "../../helpers/questionnaire-helpers";
import { getExitModalGateForPage } from "../../helpers/exit-modal";
import { useSwipeNavigation } from "../../hooks/use-swipe-navigation";
import QuestionRenderer, { isSupportedQuestion } from "./renderers";
import { getDateErrorMessage } from "./renderers/date-input";
import { getAgeFromDob, getNumberErrorMessage } from "./renderers/number-input";
import StartPage from "./start-page";
import SummaryPage from "./summary-page";
import ErrorPage from "./error-page";
import ExitConsentModal from "./exit-consent-modal";
import styled from "styled-components";
import {
  ContentCard,
  SwipeableContent,
  FixedNavBar,
  NavRow,
  NavButton,
  LoadingContainer,
  PageGroupHeader,
  QuestionTitle,
  QuestionLead,
  QuestionHelpText,
  RequiredMark,
  QuestionWrapper,
  ErrorMessage,
  SwipeHint,
} from "../../layouts/questionnaire-layout/styles";

const ReviewBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  margin-bottom: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 14px;
  color: #1e40af;
`;

const ReviewBannerButton = styled.button`
  background: #3b82f6;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: #2563eb;
  }
`;

const EMPTY_ERRORS: Set<string> = new Set();
const SELECTION_AUTO_ADVANCE_TYPES = new Set(["choice", "select"]);

function isSelectionAutoAdvanceEligible(
  qData: QuestionData,
  quickNav: boolean
): boolean {
  if (!quickNav || qData.neverAutoNext) return false;
  return SELECTION_AUTO_ADVANCE_TYPES.has(qData.type);
}

function isEnterAdvanceEligible(
  qData: QuestionData,
  quickNav: boolean
): boolean {
  if (!quickNav || qData.neverAutoNext) return false;
  return true;
}

function getHeightRangeCm(
  qData: QuestionData
): { min: number; max: number } | null {
  const choices = qData.choices || qData.options || [];
  const pattern = /^(\d+)\s*ft\s*(?:(\d+)\s*in)?$/i;
  const inches: number[] = [];
  for (const c of choices) {
    const match = ((c as any).title || (c as any).label || "").match(pattern);
    if (match)
      inches.push(parseInt(match[1], 10) * 12 + parseInt(match[2] || "0", 10));
  }
  if (inches.length === 0) return null;
  inches.sort((a, b) => a - b);
  return {
    min: Math.round(inches[0] * 2.54),
    max: Math.round(inches[inches.length - 1] * 2.54),
  };
}

function getValidationError(
  qData: QuestionData,
  answer: Answer | undefined,
  answers: AnswerMap
): string {
  if (!answer || answer.isValid) return "This field is required";

  if (
    qData.type === "date_input" ||
    qData.type === "month_date_input" ||
    qData.type === "future_date"
  ) {
    return getDateErrorMessage(qData);
  }

  if (qData.type === "number" && /weight/i.test(qData.name)) {
    const minLbs = qData.min ?? 0;
    const maxLbs = qData.max ?? 999;
    const minKg = Math.round(minLbs / 2.20462);
    const maxKg = Math.round(maxLbs / 2.20462);
    return `Please enter a value between ${minLbs}\u2013${maxLbs} lbs (${minKg}\u2013${maxKg} kg)`;
  }

  if (qData.type === "number") {
    let max = qData.max;
    if (qData.max_value_is_age) {
      const age = getAgeFromDob(answers["date_of_birth"]?.value);
      if (age != null) {
        max = max != null ? Math.min(max, age) : age;
      }
    }
    return getNumberErrorMessage(qData.min, max);
  }

  if (qData.type === "select" && /height/i.test(qData.name)) {
    const range = getHeightRangeCm(qData);
    if (range)
      return `Please enter a value between ${range.min} and ${range.max} cm`;
  }

  return "Please enter a valid value";
}

const QuestionnairePage: React.FC = () => {
  const t = useT();
  const {
    questionnaire,
    questions,
    pages,
    visiblePages,
    currentPageIndex,
    answers,
    isLoading,
    error,
    isSubmitting,
    isComplete,
    editingFromSummary,
    setEditingFromSummary,
    setScrollToQuestion,
    setAnswer,
    nextPage,
    prevPage,
    goToPage,
    submitQuestionnaire,
    restartQuestionnaire,
  } = useQuestionnaire();

  const [validationErrors, setValidationErrors] =
    useState<Set<string>>(EMPTY_ERRORS);
  const navDirectionRef = useRef<"forward" | "back">("forward");
  const contentCardRef = useRef<HTMLDivElement>(null);
  const didAutoAdvance = useRef(false);
  type ExitModalSession = { config: ExitModalConfig; questionName: string };
  const [exitModalSession, setExitModalSession] =
    useState<ExitModalSession | null>(null);
  const exitModalOpen = exitModalSession != null;
  const exitModalConfig = exitModalSession?.config ?? null;
  /** After Agree, skip exit gate for this question so `validateAndNext` matches a second Next tap. */
  const skipExitGateQuestionsRef = useRef<Set<string>>(new Set());
  const prevPhysicalPageStartRef = useRef<number | null>(null);

  const isQuestionPage =
    currentPageIndex >= 0 && currentPageIndex < visiblePages.length;

  const currentPage = isQuestionPage ? visiblePages[currentPageIndex] : null;

  useEffect(() => {
    if (!currentPage) {
      prevPhysicalPageStartRef.current = null;
      return;
    }
    const start = currentPage.startIndex;
    const prev = prevPhysicalPageStartRef.current;
    if (prev !== null && prev !== start) {
      skipExitGateQuestionsRef.current.clear();
    }
    prevPhysicalPageStartRef.current = start;
  }, [currentPage]);
  const showSummary = questionnaire?.data?.show_summary;
  const quickNav = questionnaire?.data?.quick_nav !== false;
  const isLastPage =
    isQuestionPage &&
    currentPage != null &&
    (() => {
      const pos = visiblePages.findIndex(
        (p) => p.startIndex === currentPage.startIndex
      );
      return pos === visiblePages.length - 1;
    })();

  useEffect(() => {
    if (validationErrors.size === 0) return;
    const remaining = new Set<string>();
    for (const name of validationErrors) {
      const answer = answers[name];
      if (!answer || !answer.isValid) remaining.add(name);
    }
    if (remaining.size < validationErrors.size) {
      setValidationErrors(remaining.size === 0 ? EMPTY_ERRORS : remaining);
    }
  }, [answers, validationErrors]);

  useEffect(() => {
    setValidationErrors(EMPTY_ERRORS);
    if (didAutoAdvance.current) {
      didAutoAdvance.current = false;
      contentCardRef.current?.focus();
    }
  }, [currentPageIndex]);

  useEffect(() => {
    if (currentPageIndex >= visiblePages.length && editingFromSummary) {
      setEditingFromSummary(false);
    }
  }, [
    currentPageIndex,
    visiblePages.length,
    editingFromSummary,
    setEditingFromSummary,
  ]);

  const returnToSummary = useCallback(() => {
    if (currentPage) {
      const firstQ = currentPage.questions[0];
      if (firstQ) setScrollToQuestion(firstQ.data.name);
    }
    setEditingFromSummary(false);
    goToPage(visiblePages.length);
  }, [
    currentPage,
    setScrollToQuestion,
    setEditingFromSummary,
    goToPage,
    visiblePages.length,
  ]);

  const tryAutoAdvance = useCallback(
    (pendingAnswers?: AnswerMap) => {
      if (!currentPage) return;
      if (editingFromSummary) return;
      const effectiveAnswers = pendingAnswers ?? answers;
      const errors = getPageErrors(currentPage, questions, effectiveAnswers);
      if (errors.size > 0) return;

      const exitGate = getExitModalGateForPage(
        currentPage,
        questions,
        effectiveAnswers
      );
      if (
        exitGate &&
        !skipExitGateQuestionsRef.current.has(exitGate.questionName)
      ) {
        setExitModalSession({
          config: exitGate.config,
          questionName: exitGate.questionName,
        });
        return;
      }

      const futureVisible = getVisiblePages(pages, questions, effectiveAnswers);
      const futureIdx = futureVisible.findIndex(
        (p) => p.startIndex === currentPage.startIndex
      );
      const futureIsLast =
        futureIdx < 0 || futureIdx === futureVisible.length - 1;

      if (futureIsLast && !showSummary) return;

      setValidationErrors(EMPTY_ERRORS);
      didAutoAdvance.current = true;
      navDirectionRef.current = "forward";
      if (futureIsLast && showSummary) {
        goToPage(futureVisible.length);
      } else {
        nextPage(effectiveAnswers);
      }
    },
    [
      currentPage,
      questions,
      answers,
      pages,
      showSummary,
      editingFromSummary,
      goToPage,
      nextPage,
    ]
  );

  useEffect(() => {
    if (!currentPage || !isQuestionPage) return;
    const hasSupportedQuestion = currentPage.questions.some((q) => {
      const question = questions[q.index];
      return question && isSupportedQuestion(question.data);
    });
    if (!hasSupportedQuestion) {
      currentPage.questions.forEach((q) => {
        const question = questions[q.index];
        if (question) {
          Sentry.captureMessage(
            `Unsupported question type: ${question.data.type}`,
            {
              extra: {
                questionType: question.data.type,
                questionName: question.data.name,
              },
            }
          );
        }
      });
      if (navDirectionRef.current === "back") {
        prevPage();
      } else {
        nextPage();
      }
    }
  }, [
    currentPage,
    currentPageIndex,
    isQuestionPage,
    questions,
    nextPage,
    prevPage,
  ]);

  const handlePrev = useCallback(() => {
    navDirectionRef.current = "back";
    prevPage();
  }, [prevPage]);

  /** Same forward navigation as Next after the current page passes validation (no exit modal). */
  const performForwardAfterValidPage = useCallback(() => {
    const visFwd = getVisiblePages(pages, questions, answers);
    const pos =
      currentPage != null
        ? visFwd.findIndex((p) => p.startIndex === currentPage.startIndex)
        : -1;
    const atVisEnd = pos < 0 || pos === visFwd.length - 1;

    setValidationErrors(EMPTY_ERRORS);
    navDirectionRef.current = "forward";
    if (atVisEnd) {
      if (showSummary) {
        goToPage(visFwd.length);
      } else {
        void submitQuestionnaire();
      }
    } else {
      nextPage(answers);
    }
  }, [
    currentPage,
    pages,
    questions,
    answers,
    showSummary,
    goToPage,
    nextPage,
    submitQuestionnaire,
  ]);

  const validateAndNext = useCallback(() => {
    if (!currentPage) return;
    const errors = getPageErrors(currentPage, questions, answers);
    if (errors.size > 0) {
      setValidationErrors(errors);
      return;
    }
    const exitGate = getExitModalGateForPage(currentPage, questions, answers);
    if (
      exitGate &&
      !skipExitGateQuestionsRef.current.has(exitGate.questionName)
    ) {
      setExitModalSession({
        config: exitGate.config,
        questionName: exitGate.questionName,
      });
      return;
    }
    performForwardAfterValidPage();
  }, [currentPage, questions, answers, performForwardAfterValidPage]);

  const handleExitModalAgree = useCallback(() => {
    const qn = exitModalSession?.questionName ?? null;

    if (qn) skipExitGateQuestionsRef.current.add(qn);
    setExitModalSession(null);
    validateAndNext();
  }, [
    exitModalSession,
    validateAndNext,
    currentPageIndex,
    visiblePages.length,
  ]);

  const handleExitModalDecline = useCallback(() => {
    skipExitGateQuestionsRef.current.clear();
    setExitModalSession(null);
    restartQuestionnaire();
  }, [restartQuestionnaire]);

  const { bind, style } = useSwipeNavigation({
    onNext: validateAndNext,
    onPrev: handlePrev,
    enabled: isQuestionPage && !isSubmitting,
    canGoNext: currentPageIndex < visiblePages.length,
    canGoPrev: currentPageIndex > 0,
  });

  if (isLoading) {
    return (
      <LoadingContainer>
        <div>{t("Loading questionnaire...")}</div>
      </LoadingContainer>
    );
  }

  if (error) {
    return <ErrorPage message={error} />;
  }

  if (!questionnaire || visiblePages.length === 0) {
    return <ErrorPage message="no_questions" />;
  }

  if (currentPageIndex === -1) {
    return <StartPage />;
  }

  if (currentPageIndex >= visiblePages.length) {
    if (showSummary) {
      return <SummaryPage />;
    }
    return null;
  }

  if (!currentPage) return null;

  return (
    <>
      <SwipeableContent {...bind()} style={style}>
        {editingFromSummary && (
          <ReviewBanner>
            <span>{t("You are editing your answers")}</span>
            <ReviewBannerButton onClick={returnToSummary}>
              {t("Return to Review")}
            </ReviewBannerButton>
          </ReviewBanner>
        )}
        <ContentCard
          ref={contentCardRef}
          tabIndex={-1}
          style={{ outline: "none" }}
        >
          {currentPage.group && (
            <PageGroupHeader>
              {currentPage.group.title && <h3>{currentPage.group.title}</h3>}
              {currentPage.group.help_text && (
                <p>{currentPage.group.help_text}</p>
              )}
            </PageGroupHeader>
          )}
          {currentPage.questions.map((q) => {
            const question = questions[q.index];
            if (!question) return null;
            if (!evaluateConditions(question, answers)) return null;

            const qData = question.data;
            if (!isSupportedQuestion(qData)) return null;
            const answer = answers[qData.name];
            const hasError = validationErrors.has(qData.name);

            return (
              <div key={qData.name} style={{ marginBottom: "24px" }}>
                {qData.description && (
                  <QuestionTitle>
                    {localStorage.getItem("ciq_show_question_type") ===
                      "true" &&
                      qData.type &&
                      `[${qData.type}] `}
                    {qData.description}
                    {qData.required && <RequiredMark> *</RequiredMark>}
                  </QuestionTitle>
                )}
                {qData.lead && <QuestionLead>{qData.lead}</QuestionLead>}
                {qData.help && (
                  <QuestionHelpText>{qData.help}</QuestionHelpText>
                )}
                <QuestionWrapper
                  $hasError={hasError}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (
                      e.key === "Enter" &&
                      (e.target as HTMLElement).tagName === "INPUT" &&
                      isEnterAdvanceEligible(qData, quickNav)
                    ) {
                      e.preventDefault();
                      validateAndNext();
                    }
                  }}
                >
                  <QuestionRenderer
                    question={qData}
                    answer={answer}
                    onAnswer={(value, isValid, values, autoAdvanceReady) => {
                      const newAnswer = {
                        isValid,
                        value,
                        values,
                        question,
                      };
                      setAnswer(qData.name, newAnswer);
                      if (
                        isValid &&
                        autoAdvanceReady !== false &&
                        isSelectionAutoAdvanceEligible(qData, quickNav)
                      ) {
                        tryAutoAdvance({ ...answers, [qData.name]: newAnswer });
                      }
                    }}
                  />
                  {hasError && (
                    <ErrorMessage>
                      {t(getValidationError(qData, answer, answers))}
                    </ErrorMessage>
                  )}
                </QuestionWrapper>
              </div>
            );
          })}
          {!isLastPage && (
            <SwipeHint>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
              {t("Swipe forward to continue or back to review")}
            </SwipeHint>
          )}
        </ContentCard>
      </SwipeableContent>
      <FixedNavBar>
        <NavRow>
          {currentPageIndex > 0 && (
            <NavButton variant="secondary" onClick={handlePrev}>
              {t("Back")}
            </NavButton>
          )}
          <NavButton
            variant="primary"
            onClick={validateAndNext}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("Submitting...")
              : isLastPage
              ? showSummary
                ? t("Review")
                : t("Submit")
              : t("Next")}
          </NavButton>
        </NavRow>
      </FixedNavBar>
      <ExitConsentModal
        open={exitModalOpen}
        config={exitModalConfig}
        defaultAgreeLabel={t("Agree")}
        defaultDeclineLabel={t("Decline")}
        onAgree={handleExitModalAgree}
        onDecline={handleExitModalDecline}
      />
    </>
  );
};

export default QuestionnairePage;
